import ConductionModal from "@/components/modals/ConductionModal";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI, { LiveEventProjectLists } from "@/javascript/conduction-api";
import { Project } from "@/javascript/project-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Button,
    Flex,
    ScrollArea,
    Skeleton,
    Table,
    Text,
    Tooltip
} from "@mantine/core";
import { IconAlertCircle, IconChevronsRight, IconFile, IconX } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useState } from "react";

interface LiveEventManagerProps {
    liveEventId: string;
    onClose: () => void;
}

const LiveEventManager: React.FC<LiveEventManagerProps> = ({ liveEventId, onClose }) => {
    const [projectsForReview, setprojectsForReview] = useState<Project[] | null>(null);
    const [projectPerformanceQueue, setProjectPerformanceQueue] = useState<Project[] | null>(null);
    const [projectPerformanceQueuePosition, setProjectPerformanceQueuePosition] = useState<number | null>(null);

    const auth = useContext(AuthContext);
    if (auth === undefined) {
        throw new Error("This component must be a child of AuthContext.");
    }
    const token = auth.token;
    if (token === null) {
        return;
    }

    const updateProjectLists = (projectLists: LiveEventProjectLists | null) => {
        if (!projectLists) {
            setprojectsForReview([]);
            setProjectPerformanceQueue([]);
            setProjectPerformanceQueuePosition(null);
        } else {
            const { projectsForReview, projectPerformanceQueue, projectPerformanceQueuePosition } = projectLists || {};
            // Sort projects ready for review by putting users first that are in the performance queue the least
            const sortedprojectsForReview = projectsForReview.sort((a, b) => {
                const aInQueue = projectPerformanceQueue.filter((p) => p.getUser()?.id === a.getUser()?.id).length;
                const bInQueue = projectPerformanceQueue.filter((p) => p.getUser()?.id === b.getUser()?.id).length;
                return aInQueue - bInQueue;
            });
            setprojectsForReview(sortedprojectsForReview);
            setProjectPerformanceQueue(projectPerformanceQueue);
            setProjectPerformanceQueuePosition(projectPerformanceQueuePosition);
        }
    }

    const handleChangeProjectQueuePosition = useCallback((index: number) => {
        if (projectPerformanceQueue === null || projectPerformanceQueuePosition === null) return;
        ConductionAPI.updateLiveEventQueuePosition(liveEventId, index, token)
            .then((projectLists: LiveEventProjectLists | null) => {
                updateProjectLists(projectLists);
            })
            .catch(() => {
                Utils.displayNotification(
                    "Error Changing Project Queue Position",
                    `An error occurred while changing the queue position of project ${projectPerformanceQueue[index].getName()}.`,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            });
    }, [projectPerformanceQueue, liveEventId, token]);

    const handleUnqueueProject = useCallback((project: Project) => {
        ConductionAPI.removeProjectFromQueue(liveEventId, project.getId(), token)
            .then((projectLists: LiveEventProjectLists | null) => {
                updateProjectLists(projectLists);
            })
            .catch(() => {
                Utils.displayNotification(
                    "Error Unqueuing Project",
                    `An error occurred while unqueuing project ${project.getName()}.`,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            });
    }, [liveEventId, token]);

    const handleRejectProject = useCallback((project: Project) => {
        ConductionAPI.removeProjectFromLiveEvent(liveEventId, project.getId(), token)
            .then((projectLists: LiveEventProjectLists | null) => {
                updateProjectLists(projectLists);
            })
            .catch(() => {
                Utils.displayNotification(
                    "Error Rejecting Project",
                    `An error occurred while rejecting project ${project.getName()}.`,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            });
    }, [liveEventId, token]);

    const handleQueueProject = useCallback((project: Project) => {
        ConductionAPI.approveLiveEventProject(liveEventId, project.getId(), token)
            .then((projectLists: LiveEventProjectLists | null) => {
                updateProjectLists(projectLists);
                ConductionAPI.prepareProjectInstrumentParts(project.getId(), token).catch(() => {
                    Utils.displayNotification(
                        "Error Preparing Project Parts",
                        `An error occurred while preparing project ${project.getName()} for download.`,
                        "red",
                        <IconAlertCircle size={16} />,
                        true
                    );
                });
            })
            .catch(() => {
                Utils.displayNotification(
                    "Error Queuing Project",
                    `An error occurred while queuing project ${project.getName()}.`,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            });
    }, [liveEventId, token]);

    const fetchProjectLists = useCallback(() => {
        ConductionAPI.getLiveEventProjectLists(liveEventId, token)
            .then((projectLists: LiveEventProjectLists | null) => {
                updateProjectLists(projectLists);
            })
            .catch(() => {
                Utils.displayNotification(
                    "Error Fetching Projects",
                    "An error occurred while retrieving live event projects.",
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            });
    }, [liveEventId, token]);

    useEffect(() => {
        fetchProjectLists();
        const fetchLoop = setInterval(() => {
            fetchProjectLists();
        }, 10000); // Fetch every 10 seconds
        return () => {
            clearInterval(fetchLoop);
        }
    }, []);

    const numberOfUserProjectsInQueueByUser: Record<string, number> | undefined = projectPerformanceQueue?.reduce((acc, project) => {
        const userId = project.getUser()?.id;
        if (userId) {
            acc[userId] = (acc[userId] || 0) + 1;
        }
        return acc;
    }, {} as Record<string, number>);
    const averageNumberOfProjectsInQueuePerUser = numberOfUserProjectsInQueueByUser ? Object.values(numberOfUserProjectsInQueueByUser).reduce((a, b) => a + b, 0) / Object.keys(numberOfUserProjectsInQueueByUser).length : 0;

    return (
        <ConductionModal
            title="Live Event Manager"
            onClose={onClose}
            size="xl"
        >
            <Flex
                direction={"row"}
                justify="stretch"
                style={{
                    height: `95vh`
                }}
            >
                {projectPerformanceQueue === null ? (
                    <Skeleton height={"100%"} radius="md" />
                ) : projectPerformanceQueue.length === 0 ? (
                    <Text>No projects in the performance queue.</Text>
                ) : (
                    <ScrollArea
                        style={{
                            width: "100%"
                        }}
                    >
                        <Table
                            striped
                            stickyHeader
                            highlightOnHover
                        >
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Queue</Table.Th>
                                    <Table.Th>Name</Table.Th>
                                    <Table.Th>Creator</Table.Th>
                                    <Table.Th>Actions</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {projectPerformanceQueue.map((project, index) => {
                                    const projectIsAtQueuePosition = projectPerformanceQueuePosition !== null ? projectPerformanceQueuePosition === index : false;
                                    const projectIsPastQueuePosition = projectPerformanceQueuePosition !== null ? projectPerformanceQueuePosition > index : false;
                                    return (
                                        <Table.Tr
                                            key={project.getId()}
                                            style={{
                                                backgroundColor: projectIsAtQueuePosition ? "lightgreen" : projectIsPastQueuePosition ? "gray" : ""
                                            }}
                                        >
                                            <Table.Td>
                                                <Tooltip
                                                    label={projectIsAtQueuePosition ? "This project is currently being played." : projectIsPastQueuePosition ? "Rewind the queue to this project." : "Jump to this project in the queue."}
                                                    position="top"
                                                    withArrow
                                                >
                                                    <Button
                                                        size="compact-xs"
                                                        onClick={() => handleChangeProjectQueuePosition(index)}
                                                        disabled={projectIsAtQueuePosition}
                                                    >
                                                        <IconChevronsRight size={16} />
                                                    </Button>
                                                </Tooltip>
                                            </Table.Td>
                                            <Table.Td>{project.getName()}</Table.Td>
                                            <Table.Td>
                                                {project.getUser()?.username ?? ""}
                                            </Table.Td>
                                            <Table.Td>
                                                <Button
                                                    size="xs"
                                                    onClick={() => handleUnqueueProject(project)}
                                                    disabled={projectIsPastQueuePosition}
                                                >
                                                    Unqueue
                                                </Button>
                                            </Table.Td>
                                        </Table.Tr>
                                    )
                                })}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                )}
                {projectsForReview === null ? (
                    <Skeleton height={"100%"} radius="md" />
                ) : projectsForReview.length === 0 ? (
                    <Text>No projects are ready for review.</Text>
                ) : (
                    <ScrollArea
                        style={{
                            width: "100%"
                        }}
                    >
                        <Table
                            striped
                            stickyHeader
                            highlightOnHover
                        >
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Name</Table.Th>
                                    <Table.Th>Creator</Table.Th>
                                    <Table.Th>User Queued</Table.Th>
                                    <Table.Th>Actions</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {projectsForReview.map((project) => {
                                    const numberOfUserProjectsInQueue = numberOfUserProjectsInQueueByUser ? numberOfUserProjectsInQueueByUser[project.getUser()?.id ?? ""] : 0;
                                    return (
                                        <Table.Tr
                                            key={project.getId()}
                                        >
                                            <Table.Td>{project.getName()}</Table.Td>
                                            <Table.Td>
                                                {project.getUser()?.username ?? ""}
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge color={numberOfUserProjectsInQueue >= averageNumberOfProjectsInQueuePerUser ? "yellow" : "green"}>{numberOfUserProjectsInQueue}</Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                <Button
                                                    size="xs"
                                                    onClick={() => handleQueueProject(project)}
                                                >
                                                    Queue
                                                </Button>
                                                <Tooltip
                                                    label="Reject project and unlock the creator's interface."
                                                    position="top"
                                                    withArrow
                                                >
                                                    <Button
                                                        size="xs"
                                                        onClick={() => handleRejectProject(project)}
                                                    >
                                                        <IconX size={16} />
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip
                                                    label="Open project"
                                                    position="top"
                                                    withArrow
                                                >
                                                    <Button
                                                        size="xs"
                                                        onClick={() => auth.openProject(project.getId())}
                                                    >
                                                        <IconFile size={16} />
                                                    </Button>
                                                </Tooltip>
                                            </Table.Td>
                                        </Table.Tr>
                                    )
                                })}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                )}
            </Flex>
        </ConductionModal>
    );
};

export default LiveEventManager;
